import { onBeforeUnmount, ref, watch } from "vue";
import type { Ref } from "vue";
import ShelfApi from "@/apis/shelf";
import { buildCrowdFundingPreview } from "./utils";
import type { CrowdFundingPreview } from "./utils";

function responseData(result: PromiseSettledResult<AwaitedShelfResponse>) {
  if (result.status !== "fulfilled" || String(result.value?.code) !== "0") {
    return undefined;
  }
  const data = result.value.data;
  return data && typeof data === "object" && !Array.isArray(data)
    ? data
    : undefined;
}

type AwaitedShelfResponse = {
  code?: string | number;
  data?: Record<string, any>;
};

export function usePreview(shelfId: Ref<string>) {
  const preview = ref<CrowdFundingPreview | null>(null);
  const loading = ref(false);
  const error = ref("");
  let controller: AbortController | undefined;

  async function refresh() {
    controller?.abort();
    const request = new AbortController();
    controller = request;
    preview.value = null;
    error.value = "";

    if (!shelfId.value) {
      loading.value = false;
      error.value = "链接无效，请检查货架 ID";
      return;
    }

    loading.value = true;
    try {
      const id = shelfId.value;
      const [base, universal, list, detail] = await Promise.allSettled([
        ShelfApi.getStaticDetail(id, request.signal),
        ShelfApi.getUniversalDetail(id, request.signal),
        ShelfApi.getSimpleList(id, request.signal),
        ShelfApi.getDetail(id, { publicPreview: true, signal: request.signal }),
      ]);
      if (request.signal.aborted) return;
      const staticDetail = responseData(base);
      if (!staticDetail || Object.keys(staticDetail).length === 0) {
        throw new Error("货架不存在或暂时无法加载，请重试");
      }
      preview.value = buildCrowdFundingPreview({
        staticDetail,
        detailUniversal: responseData(universal),
        simpleListRow: responseData(list)?.rows?.[0],
        shelfDetail: responseData(detail),
      });
    } catch {
      if (!request.signal.aborted) {
        error.value = "货架不存在或暂时无法加载，请重试";
      }
    } finally {
      if (!request.signal.aborted) loading.value = false;
    }
  }

  watch(shelfId, refresh, { immediate: true });
  onBeforeUnmount(() => controller?.abort());

  return { preview, loading, error, refresh };
}
